import useAirportTransferAddressSuggestions from '../hooks/useAirportTransferAddressSuggestions'
import { useQuery } from '@tanstack/react-query'
import { getAirportsSearch } from 'api/flights'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import useAirportTransferLocationSuggestions from '../hooks/useAirportTransferLocationSuggestions'
import LineMapMarkerIcon from 'LuxKit/Icons/line/LineMapMarkerIcon'
import LinePlaneDepartureIcon from 'LuxKit/Icons/line/LinePlaneDepartureIcon'
import ClickableListItem from 'LuxKit/List/ClickableListItem'
import ListItem from 'LuxKit/List/ListItem'
import { useCallback, useEffect, useMemo } from 'react'
import type { MouseEvent } from 'react'
import { useIntl } from 'react-intl'

const SEARCH_TERM_THRESHOLD = 3
const AIRPORT_ADDRESS_SEARCH_RADIUS_METRES = 250_000
const AIRPORT_COORDINATE_STALE_TIME_MS = 5 * 60 * 1000
const UNBIASED_PLACE_AUTOCOMPLETE_PARAMS = {}
const AIRPORT_ICON = <LinePlaneDepartureIcon />
const ADDRESS_ICON = <LineMapMarkerIcon />

function keepInputFocus(event: MouseEvent<HTMLButtonElement | HTMLAnchorElement>) {
  event.preventDefault()
}

interface LocationOptionProps {
  item: App.SearchItem
  onChange: (item: App.SearchItem) => void
  id?: string
  selected?: boolean
}

function LocationOption({ item, onChange, id, selected }: LocationOptionProps) {
  const onClick = useCallback(() => onChange(item), [item, onChange])
  return <ClickableListItem
    id={id}
    role={id ? 'option' : undefined}
    aria-selected={id ? selected : undefined}
    selected={selected}
    tabIndex={id ? -1 : undefined}
    onMouseDown={id ? keepInputFocus : undefined}
    title={item.format.mainText}
    subtitle={item.format.secondaryText}
    startIcon={item.searchType === 'airport' ? AIRPORT_ICON : ADDRESS_ICON}
    onClick={onClick}
    size="medium"
  />
}

interface Props {
  listboxId?: string
  listboxLabel?: string
  activeIndex?: number
  searchPhrase: string
  searchTypes: Array<App.SearchType>
  alternateAirportCode?: string
  onChange: (searchItem: App.SearchItem) => void
  onResultsChange: (results: Array<App.SearchItem>) => void
}

function CarsAirportTransferLocationSearchResults(props: Props) {
  const {
    searchPhrase,
    searchTypes,
    listboxId,
    listboxLabel,
    activeIndex = -1,
    alternateAirportCode,
    onChange,
    onResultsChange,
  } = props

  const intl = useIntl()
  const includesAddressSearch = searchTypes.includes('address')
  const includesAirportSearch = searchTypes.includes('airport')
  const shouldSearch = searchPhrase.trim().length >= SEARCH_TERM_THRESHOLD
  const normalizedAlternateAirportCode = alternateAirportCode?.toUpperCase()
  const airportSearch = useQuery({
    queryKey: ['airport-transfer-airport-coordinates', normalizedAlternateAirportCode],
    queryFn: () => getAirportsSearch(normalizedAlternateAirportCode!),
    enabled: Boolean(normalizedAlternateAirportCode),
    staleTime: AIRPORT_COORDINATE_STALE_TIME_MS,
  })
  const selectedAirport = useMemo(
    () => airportSearch.data?.find(airport => airport.airportCode === normalizedAlternateAirportCode),
    [airportSearch.data, normalizedAlternateAirportCode],
  )
  const hasAirportBias = selectedAirport?.lt !== undefined && selectedAirport.lg !== undefined
  const placeAutocompleteParams = useMemo(() => {
    if (!hasAirportBias) {
      return UNBIASED_PLACE_AUTOCOMPLETE_PARAMS
    }

    return {
      latitude: selectedAirport.lt,
      longitude: selectedAirport.lg,
      radius: AIRPORT_ADDRESS_SEARCH_RADIUS_METRES,
    }
  }, [hasAirportBias, selectedAirport])
  const addressSearchPhrase = useMemo(() => {
    if (!includesAddressSearch || !shouldSearch || (normalizedAlternateAirportCode && airportSearch.isLoading)) {
      return ''
    }

    return selectedAirport?.cityDesc ? `${searchPhrase} ${selectedAirport.cityDesc}` : searchPhrase
  }, [airportSearch.isLoading, includesAddressSearch, normalizedAlternateAirportCode, searchPhrase, selectedAirport?.cityDesc, shouldSearch])
  const typeahead = useAirportTransferLocationSuggestions(includesAirportSearch ? searchPhrase : '')
  const googlePlaces = useAirportTransferAddressSuggestions(
    addressSearchPhrase,
    placeAutocompleteParams,
  )
  const googlePredictions = useMemo(() => {
    if (!hasAirportBias) {
      return googlePlaces.predictions
    }

    return googlePlaces.predictions.filter(prediction =>
      prediction.distanceMeters === undefined || prediction.distanceMeters <= AIRPORT_ADDRESS_SEARCH_RADIUS_METRES,
    )
  }, [googlePlaces.predictions, hasAirportBias])

  const airportItems = typeahead.items
  const showAddresses = includesAddressSearch && shouldSearch &&
    (!includesAirportSearch || typeahead.isSuccess) && airportItems.length === 0
  const isAddressLoading = showAddresses && (!addressSearchPhrase || googlePlaces.isLoading)
  const addressItems = useMemo<Array<App.SearchAddress>>(() => {
    if (!showAddresses || isAddressLoading || googlePlaces.error) {
      return []
    }

    return googlePredictions.map((prediction) => ({
      searchType: 'address',
      value: prediction.placeId,
      format: {
        mainText: prediction.mainText,
        secondaryText: prediction.secondaryText,
      },
    }))
  }, [googlePredictions, googlePlaces.error, isAddressLoading, showAddresses])
  const items = useMemo(
    () => [...airportItems, ...addressItems],
    [addressItems, airportItems],
  )
  const isLoading = typeahead.isLoading || isAddressLoading
  const error = includesAirportSearch && typeahead.error || showAddresses && googlePlaces.error
  const onRetry = includesAirportSearch && typeahead.error ? typeahead.retry : googlePlaces.retry
  const hasNoResults = shouldSearch &&
    !isLoading &&
    !error &&
    (!includesAirportSearch || typeahead.isSuccess) &&
    items.length === 0

  useEffect(() => {
    onResultsChange(items)
  }, [items, onResultsChange])

  return <VerticalSpacer gap={4}>
    {isLoading && <LoadingIndicator inline />}
    {error && <>
      <ListItem title={intl.formatMessage({
        id: 'W0NkIZnT0wH-fARR17_4',
        defaultMessage: 'We couldn’t load locations. Please try again.',
        description: 'Error shown when a transfer location lookup fails, rather than returning no matches',
      })}
      />
      <ClickableListItem
        title={intl.formatMessage({
          id: 'VVyMCnOrUurxEbRVtHu5',
          defaultMessage: 'Try again',
          description: 'Action to retry the failed airport or address lookup in transfer location search',
        })}
        onClick={onRetry}
        size="medium"
      />
    </>}
    {hasNoResults && <ListItem
      title={intl.formatMessage(
        {
          id: 'ace9mkDuUniH6ixoylg7',
          defaultMessage: 'Sorry, we couldn’t find <b>{searchPhrase}</b>.',
          description: 'Error message when search phrase returns no results',
        },
        {
          searchPhrase,
          b: (chunks) => <b>{chunks}</b>,
        },
      )}
    />}
    <VerticalSpacer gap={4} id={listboxId} role={listboxId ? 'listbox' : undefined} aria-label={listboxLabel}>
      {items.map((item, index) => <LocationOption
        key={item.value}
        id={listboxId ? `${listboxId}-${index}` : undefined}
        selected={listboxId ? activeIndex === index : undefined}
        item={item}
        onChange={onChange}
      />)}
    </VerticalSpacer>
  </VerticalSpacer>
}

export default CarsAirportTransferLocationSearchResults
